import { createReducer } from '@reduxjs/toolkit';
import {
  ReCaptchaSetting,
  TrackingSetting,
  FormAuthenticatedData,
  ExternalMeasurementTagSettings,
} from 'backend';
import { GlobalErrors } from 'models';
import { useSelector } from 'react-redux';
import { FormPage, InputData } from 'shared';
import { updatePage } from '../commons';
import { RootState } from '../store';

type CurrentForm = {
  currentFormPage: FormPage | null;
  inputData: InputData;
  reCaptchaSetting?: ReCaptchaSetting;
  trackingSetting?: TrackingSetting;
  authentication?: FormAuthenticatedData;
  needsScrollTopAndFocus?: boolean;
  globalErrors?: GlobalErrors;
  externalMeasurementTag?: ExternalMeasurementTagSettings;
};

export type CurrentFormPage = CurrentForm['currentFormPage'];

export const currentFormInitialState: CurrentForm = {
  currentFormPage: null,
  inputData: {},
};

export const currentFormReducer = createReducer<CurrentForm>(
  currentFormInitialState,
  (builder) => {
    builder.addCase(updatePage, (_, action) => {
      const {
        nextFormPage,
        inputData,
        reCaptchaSetting,
        trackingSetting,
        authentication,
        needsScrollTopAndFocus,
        globalErrors,
        externalMeasurementTag,
      } = action.payload;
      return {
        currentFormPage: nextFormPage,
        inputData,
        reCaptchaSetting,
        trackingSetting,
        authentication,
        needsScrollTopAndFocus,
        globalErrors,
        externalMeasurementTag,
      };
    });
  }
);

// selector
export const useCurrentForm = (): CurrentForm =>
  useSelector<RootState, CurrentForm>((rootState) => rootState.currentForm);

export const useInputData = (): CurrentForm['inputData'] =>
  useSelector<RootState, CurrentForm['inputData']>(
    (rootState) => rootState.currentForm.inputData
  );

export const useCurrentFormPage = (): CurrentFormPage =>
  useSelector<RootState, CurrentFormPage>(
    (rootState) => rootState.currentForm.currentFormPage
  );

export const useReCaptchaSetting = (): CurrentForm['reCaptchaSetting'] =>
  useSelector<RootState, CurrentForm['reCaptchaSetting']>(
    (rootState) => rootState.currentForm.reCaptchaSetting
  );

export const useAuthentication = (): FormAuthenticatedData | undefined =>
  useSelector<RootState, FormAuthenticatedData | undefined>(
    (rootState) => rootState.currentForm.authentication
  );
